import { getUploadUrl } from 'actions/s3'
import { useUploadBrandVoiceDocumentMutation } from 'api/recoveryAi'
import { BrandVoiceDerivation } from 'api/recoveryAi.types'
import { isErrorWithMessages, isFetchBaseQueryError } from 'api/utils'
import axios from 'axios'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { useCallback, useEffect, useRef, useState } from 'react'

export type BrandVoiceUploadStatus =
  | 'idle'
  | 'uploading'
  | 'deriving'
  | 'success'
  | 'error'

// 'upload'  = presign or S3 PUT failed (nothing reached the brain);
// 'derive'  = our-fault failure (network, 5xx, deadline, or a brain that returned an
//             our-fault reason: model_output_invalid / internal);
// 'unreadable'       = the document can't be read (no text layer, parse timeout, too long);
// 'content_too_thin' = read fine, but not enough of the brand's own writing to derive a voice.
// The caller maps unreadable/content_too_thin/our-fault to three distinct copy strings.
export type BrandVoiceUploadErrorCode =
  | 'upload'
  | 'derive'
  | 'unreadable'
  | 'content_too_thin'

// Ceiling for the direct-to-S3 PUT. axios (pinned 0.18.1) defaults to no timeout,
// so a stalled TCP connection on a large/flaky upload would never settle. A hit
// throws ECONNABORTED into the catch below → 'upload' error.
const S3_UPLOAD_TIMEOUT_MS = 300_000

// The brain sends a fine-grained reason; the FE collapses it to a copy bucket.
// Unknown/absent reasons on a 422 default to 'derive' (our fault), never to a
// document-blaming bucket — proto3 can't tell "field absent" from "0", and an
// old brain, a not-yet-enumerated reason, or an unmapped interactor error all
// produce a 422 with no reason we recognize. Blaming ourselves is never wrong
// in a harmful direction; blaming the merchant's document for our bug is.
const UNREADABLE_REASONS = new Set([
  'no_text_layer',
  'parse_timeout',
  'too_long',
])
const CONTENT_TOO_THIN_REASONS = new Set(['content_too_thin', 'low_confidence'])

const reasonFrom = (error: unknown): string | undefined => {
  if (!isErrorWithMessages(error)) return undefined
  return error.data.errors.reason
}

const deriveErrorCode = (error: unknown): BrandVoiceUploadErrorCode => {
  // 5xx / network / deadline — our fault.
  if (!isFetchBaseQueryError(error) || error.status !== 422) return 'derive'
  const reason = reasonFrom(error)
  if (reason && UNREADABLE_REASONS.has(reason)) return 'unreadable'
  if (reason && CONTENT_TOO_THIN_REASONS.has(reason)) return 'content_too_thin'
  // model_output_invalid/internal (explicit our-fault), plus anything absent,
  // unrecognized, or not yet enumerated — see comment above.
  return 'derive'
}

// Belt-and-braces client watchdog on the derive POST, which is an LLM round-trip
// inside a web request. The server bounds it at 45s (gRPC deadline,
// RecoveryAiClient::DERIVATION_TIMEOUT_SECONDS); this sits comfortably above so a
// hung brain can't pin the merchant on the skeleton forever. Settles to 'error' / 'derive'.
const DERIVE_TIMEOUT_MS = 90_000

type UseBrandVoiceUpload = {
  // Resolves to the derived brand-voice text (for the caller to seed the
  // textarea) or null on any failure.
  upload: (file: File) => Promise<string | null>
  status: BrandVoiceUploadStatus
  errorCode: BrandVoiceUploadErrorCode | null
  reset: () => void
}

// Orchestrates the "Generate from documents" derive (AI-90/AI-299). Four steps:
// presigned S3 URL → raw bytes direct-to-S3 (never the JSON baseQuery) → a tiny
// {upload_key} POST that DERIVES the voice server-side and returns it → the text
// is handed back for the merchant to review. Nothing is persisted here; the
// merchant edits and saves via the config PUT. Each step fails distinctly.
const useBrandVoiceUpload = (): UseBrandVoiceUpload => {
  const merchant = useCurrentMerchant()
  const dispatch = useDispatch()
  const [acknowledge, { reset: resetMutation }] =
    useUploadBrandVoiceDocumentMutation()
  const [status, setStatus] = useState<BrandVoiceUploadStatus>('idle')
  const [errorCode, setErrorCode] = useState<BrandVoiceUploadErrorCode | null>(
    null
  )

  // The derive spans awaited steps; if the merchant closes the modal mid-flight
  // the continuation would otherwise setState on an unmounted host. Skip the
  // write when unmounted instead.
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const settle = useCallback(
    (next: BrandVoiceUploadStatus, code: BrandVoiceUploadErrorCode | null) => {
      if (!isMounted.current) return
      setStatus(next)
      setErrorCode(code)
    },
    []
  )

  const upload = useCallback(
    async (file: File): Promise<string | null> => {
      // Block re-entry for the WHOLE flow while one is in flight — spans the
      // presign, the 50 MB PUT, and the derive, so gate on our own status.
      if (status === 'uploading' || status === 'deriving') return null
      if (!merchant) {
        settle('error', 'upload')
        return null
      }

      settle('uploading', null)

      // Step 1 — presigned S3 URL, scoped to this merchant (the server's IDOR
      // guard requires the key to start with uploads/brand_voice/merchant-<id>/).
      let key: string
      let url: string
      try {
        const result = await dispatch(
          getUploadUrl({
            filename: encodeURIComponent(file.name),
            type: `brand_voice/merchant-${merchant.id}`,
            acl: 'private',
          })
        )
        if (result.error) {
          settle('error', 'upload')
          return null
        }
        const uploadUrl = result.payload.data.upload_url
        if (!uploadUrl?.url || !uploadUrl?.key) {
          settle('error', 'upload')
          return null
        }
        key = uploadUrl.key
        url = uploadUrl.url
      } catch {
        settle('error', 'upload')
        return null
      }

      // Step 2 — raw bytes DIRECT-TO-S3 (never the JSON baseQuery).
      try {
        const putResponse = await axios.put(url, file, {
          headers: { 'content-type': file.type },
          timeout: S3_UPLOAD_TIMEOUT_MS,
        })
        if (!(putResponse.status >= 200 && putResponse.status < 300)) {
          settle('error', 'upload')
          return null
        }
      } catch {
        settle('error', 'upload')
        return null
      }

      // Step 3 — DERIVE: the server derives the voice from the key and returns
      // the text (no persist). Raced against the client watchdog.
      settle('deriving', null)
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      // Hold the derive promise so a LATE rejection (server rejects AFTER the
      // watchdog already returned 'timeout') is swallowed here rather than
      // surfacing as an unhandled promise rejection.
      const derivePromise = acknowledge({ upload_key: key }).unwrap()
      derivePromise.catch(() => {})
      try {
        const outcome = await Promise.race<BrandVoiceDerivation | 'timeout'>([
          derivePromise,
          new Promise<'timeout'>(resolve => {
            timeoutId = setTimeout(() => resolve('timeout'), DERIVE_TIMEOUT_MS)
          }),
        ])
        if (outcome === 'timeout') {
          settle('error', 'derive')
          return null
        }
        const derived = outcome.brand_voice_text
        if (derived && derived.trim().length > 0) {
          settle('success', null)
          return derived
        }
        // 200 with no usable voice — the brain should never do this (it returns a
        // 422 with an :internal reason instead); treat a stray blank success as
        // our fault.
        settle('error', 'derive')
        return null
      } catch (error) {
        settle('error', deriveErrorCode(error))
        return null
      } finally {
        if (timeoutId) clearTimeout(timeoutId)
      }
    },
    [dispatch, merchant, acknowledge, status, settle]
  )

  const reset = useCallback(() => {
    setStatus('idle')
    setErrorCode(null)
    // The RTK mutation result/error is instance-scoped and survives navigation;
    // clear it too so a stale derived text or error can't carry across.
    resetMutation()
  }, [resetMutation])

  return { upload, status, errorCode, reset }
}

export default useBrandVoiceUpload
